// Экран «Бюджет» — месячный план и контроль исполнения.
//
// Порядок блоков = порядок вопросов, которые задаёшь бюджету:
//   1. Распределение — сколько дохода и сколько из него ещё не расписано.
//      Это единственная цифра, ради которой экран открывают в начале месяца.
//   2. Платежи месяца — что и когда списывается, что уже оплачено.
//   3. Категории — план против факта, с прогнозом до конца месяца.
//   4. Без плана — где тратили мимо бюджета. Без этого блока «остаток» врёт.
//
// Месяц переключается стрелками: план на следующий месяц собирается здесь же,
// кнопкой «Заполнить из <прошлого месяца>».

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/client';
import type { BudgetCategoryRow, BudgetPayment, BudgetResponse, Category } from '../api/types';
import { BudgetPaymentSheet } from '../components/BudgetPaymentSheet';
import { BudgetPlanSheet } from '../components/BudgetPlanSheet';
import { CategoryMark } from '../components/CategoryMark';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ScreenHeader } from '../components/ScreenHeader';
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilIcon,
  PlusIcon,
  RepeatIcon,
  TrashIcon,
} from '../components/icons';
import { humanError } from '../lib/errors';
import { haptic } from '../lib/telegram';
import { useToast } from '../lib/toast';
import { useBackButton } from '../lib/use-main-button';
import { useViewCurrency } from '../lib/view-currency';

type Props = { onBack: () => void };

/** Строка долгового платежа в ответе /api/budget. */
type DebtPaymentRow = BudgetResponse['debt_payments'][number];

const MONTHS_NOM = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

function periodLabel(period: string): string {
  const [y, m] = period.split('-');
  const idx = Number(m) - 1;
  return `${MONTHS_NOM[idx] ?? m} ${y}`;
}

function currentPeriod(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function Budget({ onBack }: Props): JSX.Element {
  useBackButton(onBack);
  const { formatAmount, formatShortMoney } = useViewCurrency();
  const [period, setPeriod] = useState(currentPeriod);
  const [editPlan, setEditPlan] = useState<BudgetCategoryRow | 'new' | null>(null);
  const [editPayment, setEditPayment] = useState<BudgetPayment | 'new' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<
    { kind: 'plan'; row: BudgetCategoryRow } | { kind: 'payment'; row: BudgetPayment } | null
  >(null);

  const budgetQ = useQuery({
    queryKey: ['budget', period],
    queryFn: () => api.budget(period),
  });
  const categoriesQ = useQuery({
    queryKey: ['categories', 'all-for-budget'],
    queryFn: () => api.categories(undefined, false),
  });

  const queryClient = useQueryClient();
  const toast = useToast();
  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: ['budget'] });
    queryClient.invalidateQueries({ queryKey: ['kpi'] });
    // Оплата платежа создаёт настоящий расход — список транзакций и аналитика
    // без этого показывали бы состояние до списания.
    queryClient.invalidateQueries({ queryKey: ['transactions'] });
    queryClient.invalidateQueries({ queryKey: ['analytics'] });
  };

  const copyMut = useMutation({
    mutationFn: () => api.copyBudget(budgetQ.data?.prev_period ?? '', period),
    onSuccess: (r) => {
      haptic('success');
      invalidate();
      toast.show(`Перенесено: ${r.plans} категорий, ${r.payments} платежей`);
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось перенести бюджет')),
  });

  const templateMut = useMutation({
    mutationFn: () => api.fillBudgetFromTemplate(period),
    onSuccess: (r) => {
      haptic('success');
      invalidate();
      toast.show(
        r.plans > 0
          ? `Из шаблона добавлено ${r.plans} категорий`
          : 'В шаблоне нет строк с категориями',
      );
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось заполнить из шаблона')),
  });

  const delPlanMut = useMutation({
    mutationFn: (id: string) => api.deleteBudgetPlan(id),
    onSuccess: () => {
      haptic('success');
      invalidate();
      setConfirmDelete(null);
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось удалить строку плана')),
  });

  const delPaymentMut = useMutation({
    mutationFn: (id: string) => api.deleteBudgetPayment(id),
    onSuccess: () => {
      haptic('success');
      invalidate();
      setConfirmDelete(null);
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось удалить платёж')),
  });

  const paidMut = useMutation({
    mutationFn: (v: { payment: BudgetPayment; paid: boolean }) =>
      api.setBudgetPaymentPaid(v.payment.id, v.paid),
    onSuccess: (_res, v) => {
      haptic(v.paid ? 'success' : 'light');
      invalidate();
      // Оплата двигает реальные деньги — говорим вслух, сколько и куда ушло.
      toast.show(
        v.paid
          ? `Записано ${formatShortMoney(v.payment.amount_minor)} · ${v.payment.category_name ?? v.payment.label}`
          : `Отменено: ${v.payment.label}`,
      );
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось отметить оплату')),
  });

  // Платёж по долгу необратим одним тапом: он пишет событие в debt_events, и
  // откатить его можно только коррекцией остатка. Поэтому — подтверждение.
  const [confirmDebt, setConfirmDebt] = useState<DebtPaymentRow | null>(null);
  const payDebtMut = useMutation({
    mutationFn: (d: DebtPaymentRow) =>
      api.payDebt(
        d.debt_id,
        {
          amountMinor: BigInt(d.amount_minor),
          isPrepayment: false,
          periodYm: d.period_ym,
        },
        // Ключ на (долг + месяц): повтор того же платежа за тот же месяц
        // не создаст второго события, даже если запрос уйдёт дважды.
        `budget:${d.debt_id}:${d.period_ym ?? period}`,
      ),
    onSuccess: (_res, d) => {
      haptic('success');
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['debts'] });
      queryClient.invalidateQueries({ queryKey: ['projection'] });
      toast.show(`Записано ${formatShortMoney(d.amount_minor)} · ${d.title}`);
      setConfirmDebt(null);
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось записать платёж по долгу')),
  });

  // Отмена списания живёт в тосте, а не на строке: после оплаты остаток
  // обнуляется и кнопка на строке гаснет — нажать было бы уже нечего.
  // undoMut объявлен выше payCategoryMut, потому что его же действие
  // подставляется в тост при успехе.
  const undoMut = useMutation({
    mutationFn: (txId: string) => api.deleteTransaction(txId),
    onSuccess: () => {
      haptic('light');
      invalidate();
      toast.show('Отменено');
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось отменить')),
  });

  // «Оплатить по плану»: списывает остаток категории (план − факт) одним тапом.
  const payCategoryMut = useMutation({
    mutationFn: (row: BudgetCategoryRow) =>
      api.createTransaction({
        kind: 'expense',
        amount_minor: row.remaining_minor,
        currency: 'UZS',
        category_slug: row.category_slug,
        note: `По плану: ${row.category_name ?? row.category_slug ?? ''}`.trim(),
      }),
    onSuccess: (tx, row) => {
      haptic('success');
      invalidate();
      toast.show({
        text: `Записано ${formatShortMoney(row.remaining_minor)} · ${row.category_name ?? ''}`,
        action: { label: 'Отменить', onClick: () => undoMut.mutate(tx.id) },
      });
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось списать по плану')),
  });

  const shift = (dir: -1 | 1): void => {
    const d = budgetQ.data;
    if (!d) return;
    haptic('light');
    setPeriod(dir === -1 ? d.prev_period : d.next_period);
  };

  const data = budgetQ.data;

  // Разовые обязательства и категории с непрерывными тратами живут на экране
  // по-разному: у первых вопрос «закрыто или нет», у вторых «укладываюсь ли
  // в темп». Доходы остаются в общем списке.
  const lumpRows = (data?.categories ?? []).filter(
    (r) => r.kind === 'expense' && r.spending_mode === 'lump',
  );
  const pacedRows = (data?.categories ?? []).filter(
    (r) => !(r.kind === 'expense' && r.spending_mode === 'lump'),
  );
  // Закрытым считаем, когда факт дошёл до плана или перевалил за него:
  // статусы paid и over. Частичная оплата ещё не закрывает обязательство.
  const lumpClosed = lumpRows.filter((r) => r.status === 'paid' || r.status === 'over').length;
  const lumpPlannedMinor = lumpRows.reduce((a, r) => a + BigInt(r.planned_minor), 0n).toString();
  const lumpPaidMinor = lumpRows.reduce((a, r) => a + BigInt(r.actual_minor), 0n).toString();

  return (
    <div className="screen">
      <ScreenHeader title="Бюджет" onBack={onBack} />

      <div className="period-nav">
        <button
          type="button"
          className="period-arrow"
          onClick={() => shift(-1)}
          aria-label="Предыдущий месяц"
        >
          <ChevronLeftIcon size={18} />
        </button>
        <div className="period-current">
          <div className="period-name">{periodLabel(period)}</div>
          {data ? (
            <div className="period-sub">
              {data.is_current
                ? `день ${data.days_passed} из ${data.days_in_month}`
                : data.is_future
                  ? 'планирование'
                  : 'завершён'}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          className="period-arrow"
          onClick={() => shift(1)}
          aria-label="Следующий месяц"
        >
          <ChevronRightIcon size={18} />
        </button>
      </div>

      {budgetQ.isLoading ? <div className="skeleton" style={{ height: 260 }} /> : null}
      {budgetQ.error ? (
        <div className="error">{humanError(budgetQ.error, 'Бюджет не загрузился')}</div>
      ) : null}

      {data ? (
        <>
          <Allocation data={data} />

          {!data.has_plan ? (
            <div className="empty-mini budget-empty">
              <p>На {periodLabel(period).toLowerCase()} плана ещё нет.</p>
              {data.prev_has_plan ? (
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={copyMut.isPending}
                  onClick={() => {
                    haptic('medium');
                    copyMut.mutate();
                  }}
                >
                  {copyMut.isPending
                    ? 'Переношу…'
                    : `Перенести из ${periodLabel(data.prev_period).toLowerCase()}`}
                </button>
              ) : null}
              {data.template_available ? (
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={templateMut.isPending}
                  onClick={() => {
                    haptic('medium');
                    templateMut.mutate();
                  }}
                >
                  {templateMut.isPending ? 'Заполняю…' : 'Заполнить из шаблона'}
                </button>
              ) : null}
            </div>
          ) : null}

          <SectionHeader
            title="Платежи месяца"
            action="Добавить"
            onAction={() => setEditPayment('new')}
          />
          <PaymentsBlock
            data={data}
            onToggle={(p) => paidMut.mutate({ payment: p, paid: p.paid_at == null })}
            onPayDebt={(d) => setConfirmDebt(d)}
            onEdit={(p) => setEditPayment(p)}
            onDelete={(p) => setConfirmDelete({ kind: 'payment', row: p })}
            busy={paidMut.isPending || payDebtMut.isPending}
          />

          {/* Разовые обязательства вынесены в чек-лист: после зарплаты их
              закрывают одно за другим, и главный вопрос здесь — сколько ещё
              осталось закрыть, а не сколько потрачено против темпа. */}
          {lumpRows.length > 0 ? (
            <>
              <SectionHeader title="К оплате" />
              <div className="lump-progress">
                <span>
                  Закрыто {lumpClosed} из {lumpRows.length}
                </span>
                {/* Оба числа одним форматом: «5.7M из 4.6M» невозможно сверить,
                    а на глаз выглядит так, будто закрыли больше плана. */}
                <span className="lump-progress-sum">
                  {formatAmount(lumpPaidMinor, 'UZS')} из {formatAmount(lumpPlannedMinor, 'UZS')}
                </span>
              </div>
              <div className="budget-list">
                {lumpRows.map((row) => (
                  <BudgetRow
                    key={row.plan_id}
                    row={row}
                    isFuture={data.is_future}
                    busy={payCategoryMut.isPending}
                    onPay={() => payCategoryMut.mutate(row)}
                    onEdit={() => setEditPlan(row)}
                    onDelete={() => setConfirmDelete({ kind: 'plan', row })}
                  />
                ))}
              </div>
            </>
          ) : null}

          <SectionHeader
            title="По категориям"
            action="Добавить"
            onAction={() => setEditPlan('new')}
          />
          {data.categories.length === 0 ? (
            <div className="empty-mini">Планов по категориям пока нет</div>
          ) : pacedRows.length === 0 ? (
            <div className="empty-mini">Все планы этого месяца — разовые платежи</div>
          ) : (
            <div className="budget-list">
              {pacedRows.map((row) => (
                <BudgetRow
                  key={row.plan_id}
                  row={row}
                  isFuture={data.is_future}
                  busy={payCategoryMut.isPending}
                  onPay={() => payCategoryMut.mutate(row)}
                  onEdit={() => setEditPlan(row)}
                  onDelete={() => setConfirmDelete({ kind: 'plan', row })}
                />
              ))}
            </div>
          )}

          {data.unplanned.length > 0 ? (
            <>
              <SectionHeader title="Потрачено без плана" />
              <div className="budget-list">
                {data.unplanned.map((u) => (
                  <UnplannedRow key={u.category_id ?? 'none'} row={u} />
                ))}
              </div>
            </>
          ) : null}
        </>
      ) : null}

      {editPlan ? (
        <BudgetPlanSheet
          period={period}
          initial={editPlan === 'new' ? null : editPlan}
          categories={categoriesQ.data ?? []}
          usedCategoryIds={(data?.categories ?? []).map((r) => r.category_id)}
          onClose={() => setEditPlan(null)}
          onSaved={invalidate}
        />
      ) : null}

      {editPayment ? (
        <BudgetPaymentSheet
          period={period}
          initial={editPayment === 'new' ? null : editPayment}
          categories={categoriesQ.data ?? []}
          onClose={() => setEditPayment(null)}
          onSaved={invalidate}
        />
      ) : null}

      {confirmDelete ? (
        <ConfirmDialog
          title={
            confirmDelete.kind === 'plan'
              ? `Убрать план «${confirmDelete.row.category_name ?? ''}»?`
              : `Удалить платёж «${confirmDelete.row.label}»?`
          }
          description={
            confirmDelete.kind === 'plan'
              ? 'Расходы по категории останутся в истории, исчезнет только плановая сумма.'
              : 'Запись из плана месяца исчезнет. Уже записанные операции не тронутся.'
          }
          confirmLabel="Удалить"
          destructive
          loading={delPlanMut.isPending || delPaymentMut.isPending}
          onConfirm={() =>
            confirmDelete.kind === 'plan'
              ? delPlanMut.mutate(confirmDelete.row.plan_id)
              : delPaymentMut.mutate(confirmDelete.row.id)
          }
          onCancel={() => setConfirmDelete(null)}
        />
      ) : null}

      {confirmDebt ? (
        <ConfirmDialog
          title={`Оплатить «${confirmDebt.title}»?`}
          description={`Спишется ${formatShortMoney(confirmDebt.amount_minor)}: остаток долга уменьшится, расход попадёт в баланс. Отменить одним нажатием нельзя — только коррекцией остатка в «Долгах».`}
          confirmLabel="Оплатить"
          loading={payDebtMut.isPending}
          onConfirm={() => payDebtMut.mutate(confirmDebt)}
          onCancel={() => setConfirmDebt(null)}
        />
      ) : null}
    </div>
  );
}

function SectionHeader({
  title,
  action,
  onAction,
}: {
  title: string;
  action?: string;
  onAction?: () => void;
}): JSX.Element {
  return (
    <div className="section-header">
      <div className="section-title-2">{title}</div>
      {action && onAction ? (
        <button
          type="button"
          className="link-btn link-with-icon"
          onClick={() => {
            haptic('light');
            onAction();
          }}
        >
          <PlusIcon size={14} />
          {action}
        </button>
      ) : null}
    </div>
  );
}

/** Строка распределения: куда расписан доход и сколько осталось свободным. */
function Allocation({ data }: { data: BudgetResponse }): JSX.Element {
  const { formatAmount, unitLabel } = useViewCurrency();
  const a = data.allocation;
  const income = BigInt(a.income_planned_minor);
  const debt = BigInt(a.debt_load_minor);
  const expense = BigInt(a.expense_planned_minor);
  const payments = BigInt(a.payments_unplanned_minor);
  const free = BigInt(a.unallocated_minor);
  const debtPaid = BigInt(a.debt_paid_actual_minor);

  // Ширины полос — доли от дохода. Если дохода нет, полосу не рисуем:
  // делить на ноль и показывать «100% расписано» было бы неправдой.
  const total = income > 0n ? income : 0n;
  const pct = (v: bigint): number =>
    total > 0n ? Math.max(0, Math.min(100, Number((v * 10000n) / total) / 100)) : 0;

  return (
    <section className="alloc">
      <div className="alloc-head">
        <span className="alloc-label">
          Доход по плану
          {/* Единица объявлена один раз на весь блок — дальше в столбце
              только цифры, иначе глаз ведёт по «м», а не по разрядам. */}
          <span className="alloc-unit">{unitLabel}</span>
        </span>
        <span className="alloc-income">{formatAmount(a.income_planned_minor, 'UZS')}</span>
      </div>

      {total > 0n ? (
        <div className="alloc-bar" aria-hidden="true">
          <span className="alloc-seg seg-debt" style={{ width: `${pct(debt)}%` }} />
          <span className="alloc-seg seg-expense" style={{ width: `${pct(expense)}%` }} />
          <span className="alloc-seg seg-payments" style={{ width: `${pct(payments)}%` }} />
        </div>
      ) : null}

      <div className="alloc-legend">
        <div className="alloc-item">
          <span className="alloc-dot seg-debt" />
          <span className="alloc-item-label">
            Платежи по долгам
            {debtPaid > 0n ? (
              <span className="alloc-item-sub">
                оплачено {formatAmount(debtPaid.toString(), 'UZS')}
              </span>
            ) : null}
          </span>
          <span className="alloc-item-value">{formatAmount(a.debt_load_minor, 'UZS')}</span>
        </div>
        <div className="alloc-item">
          <span className="alloc-dot seg-expense" />
          <span className="alloc-item-label">Расходы по плану</span>
          <span className="alloc-item-value">{formatAmount(a.expense_planned_minor, 'UZS')}</span>
        </div>
        {payments > 0n ? (
          <div className="alloc-item">
            <span className="alloc-dot seg-payments" />
            <span className="alloc-item-label">
              Платежи вне плана
              <span className="alloc-item-sub">нет плана по категории</span>
            </span>
            <span className="alloc-item-value">{formatAmount(payments.toString(), 'UZS')}</span>
          </div>
        ) : null}
        <div className="alloc-item">
          <span className={`alloc-dot ${free < 0n ? 'seg-over' : 'seg-free'}`} />
          <span className="alloc-item-label">{free < 0n ? 'Перерасход' : 'Не расписано'}</span>
          <span className={`alloc-item-value ${free < 0n ? 'negative' : 'positive'}`}>
            {formatAmount((free < 0n ? -free : free).toString(), 'UZS')}
          </span>
        </div>
      </div>

      {/* Факт и прогноз раньше шли двумя фразами и в короткой записи («13 млн»),
          то есть то же самое, что в строках выше, но другими словами и в другой
          точности — сравнить было нельзя. Теперь тот же столбец и тот же формат.

          Потрачено — без погашения долгов: они в строке выше, и складывать их
          сюда значило бы показать одни деньги дважды.

          Прогноз считается по категориям: разовые платежи не умножаются на
          число прошедших дней, поэтому оплата обязательств в день зарплаты
          больше не превращает нормальный месяц в «критический». */}
      <div className="alloc-facts">
        <div className="alloc-fact-row">
          <span>Потрачено</span>
          <span className="alloc-fact-value">{formatAmount(a.expense_actual_minor, 'UZS')}</span>
        </div>
        <div className="alloc-fact-row">
          <span>Получено</span>
          <span className="alloc-fact-value">{formatAmount(a.income_actual_minor, 'UZS')}</span>
        </div>
        <div className="alloc-fact-row">
          <span>Прогноз расхода за месяц</span>
          <span className="alloc-fact-value">{formatAmount(a.expense_projected_minor, 'UZS')}</span>
        </div>
      </div>
    </section>
  );
}

function PaymentsBlock({
  data,
  onToggle,
  onPayDebt,
  onEdit,
  onDelete,
  busy,
}: {
  data: BudgetResponse;
  onToggle: (p: BudgetPayment) => void;
  onPayDebt: (d: DebtPaymentRow) => void;
  onEdit: (p: BudgetPayment) => void;
  onDelete: (p: BudgetPayment) => void;
  busy: boolean;
}): JSX.Element {
  const { formatAmount } = useViewCurrency();
  const [debtsOpen, setDebtsOpen] = useState(false);
  const debtTotal = data.debt_payments.reduce((acc, d) => acc + BigInt(d.amount_minor), 0n);
  const debtUnpaid = data.debt_payments.filter((d) => d.current_month_status !== 'paid');

  if (data.payments.length === 0 && data.debt_payments.length === 0) {
    return <div className="empty-mini">Платежей на месяц не заведено</div>;
  }

  return (
    <>
      {data.payments.length > 0 ? (
        <div className="pay-summary">
          <span>
            Оплачено {formatAmount(data.payments_paid_minor, 'UZS')} из{' '}
            {formatAmount(data.payments_total_minor, 'UZS')}
          </span>
        </div>
      ) : null}

      <div className="budget-list">
        {data.payments.map((p) => (
          <div key={p.id} className={`pay-row ${p.paid_at ? 'paid' : ''}`}>
            <div className="pay-day">{p.due_day ?? '—'}</div>

            <div className="pay-main">
              <div className="pay-label">
                <span className="pay-label-text">{p.label}</span>
                {p.is_recurring ? (
                  <span className="pay-repeat" title="Повторяется каждый месяц">
                    <RepeatIcon size={12} />
                  </span>
                ) : null}
              </div>
              <div className="pay-meta">
                {p.category_name ?? 'Без категории'}
                {p.is_overdue ? <span className="pay-overdue"> · срок прошёл</span> : null}
                {p.paid_tx_deleted ? <span className="pay-tx-gone"> · расход удалён</span> : null}
              </div>
            </div>

            <div className="pay-amount">
              {/* Заплатить могли не ровно по плану — показываем факт, план мельче. */}
              {p.paid_amount_minor && p.paid_amount_minor !== p.amount_minor ? (
                <>
                  {formatAmount(p.paid_amount_minor, 'UZS')}
                  <span className="pay-amount-plan">
                    план {formatAmount(p.amount_minor, 'UZS')}
                  </span>
                </>
              ) : (
                formatAmount(p.amount_minor, 'UZS')
              )}
            </div>

            <div className="pay-act">
              {/* Кружок-галочку заменили явной кнопкой: по нему не было видно,
                  что он списывает деньги. */}
              {p.paid_at ? (
                <button
                  type="button"
                  className="pay-btn done"
                  disabled={busy}
                  onClick={() => onToggle(p)}
                  title="Отменить оплату и удалить расход"
                >
                  <CheckIcon size={14} />
                  Оплачено
                </button>
              ) : (
                <button
                  type="button"
                  className="pay-btn"
                  disabled={busy}
                  onClick={() => onToggle(p)}
                >
                  Оплатить
                </button>
              )}
              <button
                type="button"
                className="icon-btn"
                onClick={() => onEdit(p)}
                aria-label="Изменить"
              >
                <PencilIcon size={16} />
              </button>
              <button
                type="button"
                className="icon-btn"
                onClick={() => onDelete(p)}
                aria-label="Удалить"
              >
                <TrashIcon size={16} />
              </button>
            </div>
          </div>
        ))}

        {data.debt_payments.length > 0 ? (
          <>
            <button
              type="button"
              className={`debt-fold ${debtsOpen ? 'open' : ''}`}
              onClick={() => {
                haptic('light');
                setDebtsOpen((v) => !v);
              }}
              aria-expanded={debtsOpen}
            >
              <span className="debt-fold-chevron">
                <ChevronRightIcon size={16} />
              </span>
              <span className="debt-fold-main">
                <span className="pay-label">Платежи по долгам</span>
                <span className="pay-meta">
                  {debtUnpaid.length > 0
                    ? `${debtUnpaid.length} из ${data.debt_payments.length} ждут оплаты`
                    : `${data.debt_payments.length} позиций · всё оплачено`}
                </span>
              </span>
              <span className="pay-amount">{formatAmount(debtTotal.toString(), 'UZS')}</span>
            </button>

            {debtsOpen
              ? data.debt_payments.map((d) => {
                  const isPaid = d.current_month_status === 'paid';
                  return (
                    <div key={d.debt_id} className={`pay-row nested ${isPaid ? 'paid' : ''}`}>
                      <div className="pay-day">{d.payment_day ?? '—'}</div>
                      <div className="pay-main">
                        <div className="pay-label">{d.title}</div>
                        <div className="pay-meta">
                          {d.current_month_status === 'overdue' ? (
                            <span className="pay-overdue">пропущен платёж</span>
                          ) : isPaid ? (
                            'за этот месяц оплачен'
                          ) : (
                            'сумма и график — в «Долгах»'
                          )}
                        </div>
                      </div>
                      <div className="pay-amount">{formatAmount(d.amount_minor, 'UZS')}</div>
                      <div className="pay-act">
                        {/* Платёж по долгу одним тапом не отменить (событие в
                            debt_events правится только коррекцией), поэтому
                            здесь — подтверждение, в отличие от плановых. */}
                        <button
                          type="button"
                          className={`pay-btn ${isPaid ? 'done' : ''}`}
                          disabled={busy || isPaid}
                          onClick={() => onPayDebt(d)}
                        >
                          {isPaid ? <CheckIcon size={14} /> : null}
                          {isPaid ? 'Оплачено' : 'Оплатить'}
                        </button>
                      </div>
                    </div>
                  );
                })
              : null}
          </>
        ) : null}
      </div>
    </>
  );
}

function BudgetRow({
  row,
  isFuture,
  busy,
  onPay,
  onEdit,
  onDelete,
}: {
  row: BudgetCategoryRow;
  isFuture: boolean;
  busy: boolean;
  onPay: () => void;
  onEdit: () => void;
  onDelete: () => void;
}): JSX.Element {
  const { formatAmount, formatMoney } = useViewCurrency();
  const usedPct = Math.min(100, Math.max(0, row.used_pct));
  const remaining = BigInt(row.remaining_minor);
  const isIncome = row.kind === 'income';

  // «Оплатить по плану» списывает остаток категории одним тапом.
  // Не показываем, когда списывать нечего, для доходов («оплатить» зарплату
  // бессмысленно) и для погашения долгов — там расход создаётся платежом по
  // конкретному долгу, иначе получилась бы транзакция, ни к чему не привязанная.
  const canPay = !isIncome && !row.covered_by_debts && remaining > 0n;

  return (
    <div className={`budget-row budget-${row.status}`}>
      <div className="budget-head">
        <CategoryMark name={row.category_name} slug={row.category_slug} size="sm" />
        <div className="budget-main">
          <div className="budget-name">{row.category_name ?? row.category_slug}</div>
          <div className="budget-amounts">
            <b>{formatAmount(row.actual_minor, 'UZS')}</b>
            <span className="muted"> из {formatAmount(row.planned_minor, 'UZS')}</span>
          </div>
        </div>
        <div className="budget-actions">
          <button type="button" className="icon-btn" onClick={onEdit} aria-label="Изменить">
            <PencilIcon size={16} />
          </button>
          <button type="button" className="icon-btn" onClick={onDelete} aria-label="Удалить">
            <TrashIcon size={16} />
          </button>
        </div>
      </div>

      <div className="budget-bar">
        <div className="budget-bar-fill" style={{ width: `${usedPct}%` }} />
      </div>

      <div className="budget-meta">
        <span>
          {row.status === 'paid'
            ? 'закрыто'
            : remaining >= 0n
              ? `осталось ${formatAmount(remaining.toString(), 'UZS')}`
              : `сверх плана ${formatAmount((-remaining).toString(), 'UZS')}`}
        </span>
        {/* Процент прогноза показываем только там, где темп определён. У разового
            платежа он всегда равен 100% плана и не несёт информации. */}
        {!isFuture && !isIncome && row.spending_mode === 'spread' && row.planned_minor !== '0' ? (
          <span className={row.projected_pct > 100 ? 'warn' : ''}>
            прогноз {row.projected_pct.toFixed(0)}%
          </span>
        ) : null}
      </div>

      {/* Молча выкидывать план из суммы нельзя — объясняем, почему он не
          участвует в «не расписано». */}
      {row.covered_by_debts ? (
        <div className="budget-covered">
          не входит в расходы — долги считаются строкой «Платежи по долгам»
        </div>
      ) : null}

      {canPay ? (
        <div className="budget-act">
          {/* Сумму пишем прямо на кнопке: тап списывает деньги сразу, и
              пользователь должен видеть сколько, ещё не нажав. */}
          <button type="button" className="pay-btn" disabled={busy} onClick={onPay}>
            Оплатить {formatMoney(row.remaining_minor, 'UZS')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function UnplannedRow({
  row,
}: {
  row: {
    category_id: string | null;
    category_slug: string | null;
    category_name: string | null;
    actual_minor: string;
  };
}): JSX.Element {
  const { formatAmount } = useViewCurrency();
  return (
    <div className="unplanned-row">
      {/* Именно slug, не id: тинт монограммы берётся из хеша slug, и с uuid
          та же категория красилась здесь иначе, чем на остальном экране. */}
      <CategoryMark name={row.category_name} slug={row.category_slug} size="sm" />
      <div className="budget-main">
        <div className="budget-name">{row.category_name ?? 'Без категории'}</div>
        <div className="budget-meta-inline">плана на месяц нет</div>
      </div>
      <div className="pay-amount">{formatAmount(row.actual_minor, 'UZS')}</div>
    </div>
  );
}

export type { Category };
